/**
 * src/app/modules/authentication/services/authentication.interceptor.ts
 *
 * @author Bryan Henry <bryan@payfully.co>
 * @date 4/27/2021
 * @copyright Payfully, 2021
 */

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { sessionExpirationTimeKey } from '../../../services/auto-logout.service';
import { ApiError } from '../../shared/models/api-error';
import { AuthenticationService } from './authentication.service';

export const SESSION_EXPIRED_MESSAGE =
  'Session may have expired. Please sign in again.';

const SESSION_EXPIRED_MESSAGE_LAST_SHOWN_KEY = 'sessionExpiredMessageLastShown';
const SESSION_EXPIRED_MESSAGE_DEBOUNCE_MS = 5 * 60 * 1000; // 5 minutes

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private toastrService: ToastrService,
  ) {}

  private shouldShowSessionExpiredMessage(): boolean {
    try {
      const lastShownStr = localStorage.getItem(
        SESSION_EXPIRED_MESSAGE_LAST_SHOWN_KEY,
      );
      if (!lastShownStr) {
        return true;
      }

      const lastShown = parseInt(lastShownStr, 10);
      if (Number.isNaN(lastShown)) {
        this.clearSessionExpiredMessageFlag();
        return true;
      }

      const now = Date.now();
      const timeSinceLastShown = now - lastShown;
      return timeSinceLastShown >= SESSION_EXPIRED_MESSAGE_DEBOUNCE_MS;
    } catch {
      return true;
    }
  }

  private markSessionExpiredMessageShown(): void {
    try {
      localStorage.setItem(
        SESSION_EXPIRED_MESSAGE_LAST_SHOWN_KEY,
        Date.now().toString(),
      );
    } catch {
      // Storage unavailable (e.g. private mode); no-op.
    }
  }

  private clearSessionExpiredMessageFlag(): void {
    try {
      localStorage.removeItem(SESSION_EXPIRED_MESSAGE_LAST_SHOWN_KEY);
    } catch {
      // Storage unavailable; no-op.
    }
  }

  /**
   * Handles 401 Unauthorized errors consistently across all backend versions.
   * Shows debounced message, logs out user, and redirects to login.
   */
  private handle401Error(error: HttpErrorResponse): Observable<never> {
    if (this.authenticationService.skip401Redirect(location.pathname)) {
      const shouldShowMessage = this.shouldShowSessionExpiredMessage();
      if (shouldShowMessage) {
        this.toastrService.error(SESSION_EXPIRED_MESSAGE);
      }

      this.authenticationService.logoutSuccess(true);

      // Mark as shown *after* logoutSuccess(true) clears localStorage,
      // so the debounce timestamp is preserved and works across tabs.
      if (shouldShowMessage) {
        this.markSessionExpiredMessageShown();
      }

      this.router.navigate(['/login'], {
        queryParams: { returnUrl: this.router.url },
      });
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            statusText: error.statusText,
            url: error.url ?? undefined,
            error: { message: SESSION_EXPIRED_MESSAGE },
          }),
      );
    }
    return throwError(() => error);
  }

  /**
   * Handles backend version 1 specific error translation for 400 and 500 errors.
   */
  private handleBackendV1Error(error: HttpErrorResponse): Observable<never> {
    if ((error.status === 400 || error.status === 500) && error.error?.error) {
      const translatedError: ApiError = {
        message: error.error.error,
        messageCode: undefined,
        code: undefined,
      };
      if (error.error.message) {
        translatedError.message = error.error.message;
      }
      if (error.error.messageCode) {
        translatedError.messageCode = error.error.messageCode;
      }
      if (error.error.code) {
        translatedError.code = error.error.code;
      }

      const translatedResponse = new HttpErrorResponse({
        statusText: error.statusText,
        url: error.url ?? undefined,
        status: error.status,
        headers: error.headers,
        error: translatedError,
      });

      if (!environment.production) {
        // tslint:disable-next-line:no-console
        console.debug('translated payload from error to message.');
      }

      return throwError(() => translatedResponse);
    }

    return throwError(() => error);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          const expirationTime = event.headers.get(sessionExpirationTimeKey);
          if (expirationTime) {
            // Clear flag only when session is refreshed (new expiration time received).
            // This allows the message to show again after re-login, while the time-based
            // debounce prevents spam across multiple tabs.
            this.clearSessionExpiredMessageFlag();
            localStorage.setItem(sessionExpirationTimeKey, expirationTime);
          }
        }
      }),
      catchError((e) => {
        if (!(e instanceof HttpErrorResponse)) {
          return throwError(() => e);
        }

        // Handle 401 errors consistently across all backend versions
        if (e.status === 401) {
          return this.handle401Error(e);
        }

        // Handle backend version 1 specific error translation
        if (environment.backendVersion === 1) {
          return this.handleBackendV1Error(e);
        }

        return throwError(() => e);
      }),
    );
  }
}
